/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: usePermissions.ts
 🗓️ Created: 2025-01-06
 🧩 Description:
    Custom hook for managing user permissions and role-based access control.
    Uses slug-based string comparison — no numeric ID resolution needed.

 🔁 Last Updated: 2025-02-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useAuthStore } from '../store/auth';
import { useMemo } from 'react';
import { resolveModuleSlug, MODULE_SLUGS } from '../services/moduleIds';

export interface Permission {
  moduleSlug: string;
  moduleName: string;
  level: 'NONE' | 'READ' | 'WRITE' | 'FULL';
}

export interface ModulePermission {
  moduleSlug: string;
  level: 'NONE' | 'READ' | 'WRITE' | 'FULL';
}

// Re-export MODULE_SLUGS for convenience
export { MODULE_SLUGS } from '../services/moduleIds';

export const usePermissions = () => {
  const { user } = useAuthStore();

  // Get all permissions for the current user (slug-based)
  const userPermissions = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles)) {
      return [];
    }

    const allPermissions: ModulePermission[] = [];

    user.roles.forEach((role) => {
      if (role.permissions && Array.isArray(role.permissions)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        role.permissions.forEach((permission: any) => {
          // Support both moduleSlug and legacy moduleId
          const slug = permission.moduleSlug || permission.moduleName || String(permission.moduleId || '');
          if (slug) {
            allPermissions.push({
              moduleSlug: slug,
              level: permission.level as 'NONE' | 'READ' | 'WRITE' | 'FULL'
            });
          }
        });
      }
    });

    return allPermissions;
  }, [user?.roles]);

  /**
   * Check if user has permission for a specific module
   * @param moduleIdentifier - Module slug string or MODULE_SLUGS key
   * @param requiredLevel - Required permission level
   * @returns boolean - Whether user has permission (synchronous)
   */
  const hasModulePermission = (moduleIdentifier: string | number, requiredLevel: 'READ' | 'WRITE' | 'FULL' = 'READ'): boolean => {
    if (!user) {
      return false;
    }

    // 🛡️ SECURITY BYPASS: Primary admins have full access to everything globally
    if (user.isPrimaryAdmin) {
      return true;
    }

    if (!userPermissions.length) {
      return false;
    }

    // Resolve identifier to slug
    const slug = resolveModuleSlug(String(moduleIdentifier));
    if (!slug) {
      return false;
    }

    const modulePermission = userPermissions.find(p => p.moduleSlug === slug);

    if (!modulePermission) {
      return false;
    }

    // Permission hierarchy: NONE < READ < WRITE < FULL
    const levelHierarchy: Record<string, number> = {
      'NONE': 0,
      'READ': 1,
      'WRITE': 2,
      'FULL': 3
    };

    const userLevel = levelHierarchy[modulePermission.level] ?? 0;
    const requiredLevelValue = levelHierarchy[requiredLevel] ?? 0;

    return userLevel >= requiredLevelValue;
  };

  /**
   * Check if user has any permission for a module
   */
  const hasAnyModulePermission = (moduleIdentifier: string | number): boolean => {
    return hasModulePermission(moduleIdentifier, 'READ');
  };

  // Check if user can write to a module
  const canWriteModule = (moduleIdentifier: string | number): boolean => {
    return hasModulePermission(moduleIdentifier, 'WRITE');
  };

  // Check if user has full access to a module
  const hasFullModuleAccess = (moduleIdentifier: string | number): boolean => {
    return hasModulePermission(moduleIdentifier, 'FULL');
  };

  // Get user's permission level for a module
  const getModulePermissionLevel = (moduleIdentifier: string | number): 'NONE' | 'READ' | 'WRITE' | 'FULL' => {
    if (!user) return 'NONE';
    if (user.isPrimaryAdmin) return 'FULL';
    if (!userPermissions.length) return 'NONE';

    const slug = resolveModuleSlug(String(moduleIdentifier));
    if (!slug) return 'NONE';

    const modulePermission = userPermissions.find(p => p.moduleSlug === slug);
    return modulePermission?.level || 'NONE';
  };

  // Check if user is admin (has full access to most modules)
  const isAdmin = (): boolean => {
    if (!user) return false;
    if (user.isPrimaryAdmin) return true;
    if (!userPermissions.length) return false;

    const adminModules = [
      'USERS',
      'PROFILES',
      'ROLE_PROFILES',
      'TENANTS'
    ];

    return adminModules.every(moduleKey => hasFullModuleAccess(moduleKey));
  };

  // Get modules that user has access to
  const getAccessibleModules = (): string[] => {
    if (!user || !userPermissions.length) return [];

    return userPermissions
      .filter(p => p.level !== 'NONE')
      .map(p => p.moduleSlug);
  };

  // Check if user can access a specific route
  const canAccessRoute = (route: string): boolean => {
    const routeModuleMap: Record<string, string> = {
      '/dashboard': 'DASHBOARD',
      '/users': 'USERS',
      '/profiles': 'ROLE_PROFILES',
      '/role-profiles': 'ROLE_PROFILES',
      '/log-profiles': 'LOG_PROFILES',
      '/admin/admin/app-keys': 'API_KEYS',
      '/admin/system-settings/telephony-servers': 'TELEPHONY_SERVERS',
      '/admin/multi-tenant/domains': 'TELEPHONY_DOMAINS',
      '/admin/multi-tenant/tenants': 'TENANTS',
      '/admin/multi-tenant/branding': 'BRANDING',
      '/admin/system-settings/misc': 'SYSTEM_MISC',
      '/admin/system-settings/email': 'EMAIL_SETTINGS',
      '/settings/pbx/log-files': 'LOG_SETTINGS',
      '/reports/system/audit-logs': 'AUDIT_LOGS',
      '/reports/system/user-activity': 'USER_ACTIVITY',
      '/reports/system/change-history': 'CHANGE_HISTORY',
      '/reports/system/security': 'SECURITY_LOGS',
      '/settings/appearance': 'APPEARANCE',
      '/settings/languages': 'APPEARANCE',
      '/profile': 'USER_PROFILE',
      '/pbx/calls-routing/gateways': 'PBX_CALLS_ROUTING_GATEWAYS',
      '/settings/technology/sip': 'SIP_SETTINGS',
      '/settings/technology/dial-profile': 'SETTINGS_TECH_DIAL_PROFILE',
      '/settings/pbx/voicemail-settings': 'VOICEMAIL_SETTINGS',
      '/settings/pbx/cdr-settings': 'CDR_SETTINGS',
      '/reports/cdr/filters': 'CDR_FILTERS',
      '/reports/cdr/cdr': 'CALL_DETAIL_RECORDS',
      '/settings/pbx/system-vars': 'SETTINGS_PBX_SYSTEM_VARS',
      '/settings/voice-prompts/recording': 'SETTINGS_VOICE_PROMPTS_RECORDING',
      '/settings/voice-prompts/recording/new': 'SETTINGS_VOICE_PROMPTS_RECORDING',
      '/settings/voice-prompts/recording/:id': 'SETTINGS_VOICE_PROMPTS_RECORDING',
      '/settings/integrations': 'INTEGRATIONS',
    };

    const moduleKey = routeModuleMap[route];
    if (!moduleKey) return false;

    return hasAnyModulePermission(moduleKey);
  };

  return {
    userPermissions,
    hasModulePermission,
    hasAnyModulePermission,
    canWriteModule,
    hasFullModuleAccess,
    getModulePermissionLevel,
    isAdmin,
    getAccessibleModules,
    canAccessRoute,
    MODULE_SLUGS
  };
};
